import type { ControlLockLease } from '@ctcells/contract'
import { REQUEST_TIMEOUT_MS, useSessionStore } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import {
  applyControlLockState,
  canApplyAcquiredLease,
  clearControlLock,
  controlLockRevision,
  getControlLockSnapshot,
  markControlLockUnknown,
  setControlLease,
} from '../controlToken.ts'
import { keys } from '../keys.ts'

/**
 * 제어락(IFC-RO-02).
 *
 * 🔴 **리스 토큰은 응답으로만 안다.** 획득 응답이 유실되면 토큰을 모르는 보유자가 생기므로
 *    계약이 `command_id` dedup 으로 「같은 요청이면 기존 리스를 그대로 재반환」을 보장한다.
 *    그래서 재시도해도 안전하고, **`command_id` 를 새로 만들지 않는 것**이 중요하다.
 * 🔴 반납·heartbeat 는 `X-Control-Token` 을 요구한다 — 클라이언트가 자동으로 붙인다(`controlToken.ts`).
 * ⚠ 409 `CONTROL_LOCK_HELD` 처리는 **여기서 하지 않는다** — 클라이언트(`client.ts`)가 모든
 *   쓰기 응답을 보고 보관소에 반영한다. 명령 훅마다 손으로 다는 자리가 아니다.
 */

let snapshotRequest = 0

export async function refreshControlLockState(): Promise<void> {
  if (useSessionStore.getState().status !== 'authenticated') return
  const requestId = ++snapshotRequest
  const startRevision = controlLockRevision()
  try {
    const state = await orchestrator.request<unknown>({
      method: 'GET',
      path: '/control-token',
      retryOn401: true,
    })
    if (requestId === snapshotRequest) applyControlLockState(state, startRevision)
  } catch {
    if (requestId === snapshotRequest && controlLockRevision() === startRevision)
      markControlLockUnknown()
  }
}

export const useAcquireControlLock = createCommand<{ commandId?: string }, ControlLockLease>({
  send: async ({ commandId }) => {
    const startRevision = controlLockRevision()
    const sessionId = useSessionStore.getState().sessionId
    const lease = await orchestrator.request<ControlLockLease>({
      method: 'POST',
      path: '/control-token',
      body: { command_id: commandId ?? newCommandId() },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    })
    if (canApplyAcquiredLease(startRevision, sessionId, lease.acquired_at)) setControlLease(lease)
    return lease
  },
  // 🔴 락을 쥐면 **서버가 주는 `allowed_commands` 가 통째로 달라진다**(백엔드
  //    `services/batches.allowed_commands` 는 `holds_lock` 이 거짓이면 빈 배열을 준다).
  //    낙관적으로 버튼을 켜지 않고 **무효화해서 서버에 다시 묻는다**.
  invalidates: () => [keys.modules.all(), keys.batches.all()],
})

/**
 * 반납 · 강제 해제.
 *
 * 🔴 **본문이 `IrreversibleCommandEnvelope` 라 `reason` 이 필수다**(실서버 스펙 ·
 *    `views/control_token.py` — 사유가 `audit_log.detail.reason` 으로 해시체인에 봉인된다).
 *    빠뜨리면 422 다. 화면이 사유를 받아 넘긴다 — 합성하지 않는다.
 * ⚠ `force=true` 는 admin 한정이고(서버가 한 번 더 본다) 감사 낱말이 갈린다(`control_lock_force`).
 */
export const useReleaseControlLock = createCommand<
  { reason: string; force?: boolean; commandId?: string },
  void
>({
  send: async ({ reason, force, commandId }) => {
    const startRevision = controlLockRevision()
    const startToken = getControlLockSnapshot().lease?.token
    await orchestrator.request<void>({
      method: 'DELETE',
      path: '/control-token',
      query: force === undefined ? {} : { force },
      body: { command_id: commandId ?? newCommandId(), reason },
      timeoutMs: REQUEST_TIMEOUT_MS.command,
      retryOn401: true,
    })
    /*
     * 내가 반납했다 — 그 사이 회수 사건(`held: false`)이 먼저 와 «만료»로 섰어도 내가 놓은 것이라 «없음»이 맞다(2026-10-23).
     * 다른 보유자(`other`)가 그 사이 섰으면 그대로 둔다.
     * 🔴 **반납 중에 하트비트가 겹쳐도 «없음»이다**(히나 점검 2026-10-23) — 하트비트 응답이 같은 토큰으로 보관소를 다시 쓰면
     *    판(revision)이 바뀌어 종전 조건으로는 «내가 보유»가 남았다. 서버는 이미 놓았다(204). 같은 토큰이면 내 반납이 이긴다.
     */
    const now = getControlLockSnapshot()
    if (
      controlLockRevision() === startRevision ||
      now.status === 'expired' ||
      (now.status === 'mine' && startToken !== undefined && now.lease?.token === startToken)
    )
      clearControlLock()
  },
  invalidates: () => [keys.modules.all(), keys.batches.all()],
})

/**
 * 리스 연장.
 *
 * ⚠ **주기 폴링이 아니다**(DP-01 무관) — 서버 상태를 긁는 것이 아니라 내가 쥔 **리스의 수명을
 *   미는 명령**이다. 락이 없으면 한 번도 나가지 않는다. 주기는 리스가 정한다(`useControlLock.ts`).
 * ⚠ **본문이 없다.** 실서버 스펙에 `requestBody` 가 없고 `command_id` 도 받지 않는다
 *   (`views/control_token.py` — 「본문이 없으므로 command_id 도 없다」). 멱등키를 실어 보내지 않는다.
 * ⚠ **훅이 아니다.** 타이머가 부르는 자리라 React 밖에서도 불려야 한다. 캐시도 건드리지 않는다
 *   (연장은 서버 상태를 바꾸지 않는다 — 무효화할 것이 없다).
 *
 * 🔴 **`retryOn401: true`** — eric 확정 2026-09-21 「화면을 보는 중에 만료됨이 떴다」 진단 결과.
 *    Access token TTL 900초(`ACCESS_TOKEN_TTL_SEC` 기본값 · 실측)의 80% 지점에 세션이 조용히
 *    갱신하지만(`useSessionBootstrap`), 그 타이머도 `setTimeout` 이라 탭이 백그라운드에서
 *    스로틀되면 **같이** 늦는다. 이 하트비트는 10초 간격(TTL 30초의 1/3)이라 access token 쪽보다
 *    훨씬 촘촘히 돈다 — 그 사이에 access token 이 만료 상태로 걸리면 **인증(401)** 이 먼저 막고,
 *    이 요청은 `retryOn401` 이 없어 재시도(refresh) 기회 없이 그대로 실패해 리스가 `expired` 로
 *    떨어졌다(재현: `curl` 로 조작한 access token + 유효한 `X-Control-Token` → 401 `AUTH_FAILED`,
 *    2026-09-21). 하트비트는 `X-Control-Token` 으로 리스를 식별하는 멱등 연산이라 재시도해도
 *    이중 집행 위험이 없다 — GET 과 같은 성격으로 대해도 된다.
 */
export function heartbeatControlLock(): Promise<ControlLockLease> {
  return orchestrator.request<ControlLockLease>({
    method: 'POST',
    path: '/control-token/heartbeat',
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}
